import { css } from '@emotion/react';
import { breakpoints } from '@frontend/responsiveness';
import { classNames } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Heading } from '@frontend/design-system';
import { useCareCreditTranslations } from '../../../../hooks/use-care-credit-translations';
import { useDisplayCareCreditData } from '../../../../hooks/use-display-care-credit-data';
import { CareCreditPatientData } from '../../../../types/care-credit.types';

const styles = {
  card: css`
    padding: ${theme.spacing(2)};
    display: flex;
    gap: ${theme.spacing(2)};
    background: ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.medium};
    flex-direction: column;
  `,
  items: css`
    display: flex;
    gap: ${theme.spacing(6)};

    @media (max-width: ${breakpoints.small.min}px) {
      flex-wrap: wrap;
      gap: ${theme.spacing(2)};
    }
  `,
  item: css`
    min-width: 90px;
    .item {
      &__label {
        font-size: ${theme.font.size.small};
        color: ${theme.colors.neutral50};
        white-space: nowrap;
      }
      &__value {
        font-size: ${theme.font.size.large};
        color: ${theme.colors.neutral90};
      }
    }
  `,
};

export type AccountDetailsCardProps = {
  careCreditData: CareCreditPatientData;
  className?: string;
};
export const AccountDetailsCard = ({ careCreditData, className }: AccountDetailsCardProps) => {
  const { t } = useCareCreditTranslations();

  const { formattedAmount } = useDisplayCareCreditData(careCreditData);
  const normalizedAccountNumber = careCreditData.accountNumber?.replace(/\D/g, '') || '';
  const maskedAccountNumber = normalizedAccountNumber
    ? `${'*'.repeat(Math.max(normalizedAccountNumber.length - 4, 0))}${normalizedAccountNumber.slice(-4)}`
    : '';

  return (
    <div className={classNames('account-details-card', className)} css={styles.card}>
      <Heading level={3}>{t('Account Details')}</Heading>
      <div className='account-details-card__items' css={styles.items}>
        <div className='account-details-card__item' css={styles.item}>
          <div className='item__label'>{t('Transaction Type')}</div>
          <div className='item__value'>{t('Purchase')}</div>
        </div>
        <div className='account-details-card__item' css={styles.item}>
          <div className='item__label'>{t('Available Credit')}</div>
          <div className='item__value'>{formattedAmount}</div>
        </div>
        <div className='account-details-card__item' css={styles.item}>
          <div className='item__label'>{t('Account Number')}</div>
          <div className='item__value'>{maskedAccountNumber}</div>
        </div>
      </div>
    </div>
  );
};
